/**
 * Utility functions for document display formatting
 */

/**
 * Format document title to avoid double display when title equals fileName.
 *
 * Logic:
 * - If title and fileName are the same (case-insensitive), return title only
 * - If title equals fileName without extension, return title only
 * - Otherwise, return "title - fileName" format
 *
 * @param title - LLM-generated or document title
 * @param fileName - Original file name
 * @returns Formatted title string
 *
 * @example
 * formatDocumentTitle('Report.PDF', 'report.pdf') // => 'Report.PDF'
 * formatDocumentTitle('設計基準書', '設計基準書.pdf') // => '設計基準書'
 * formatDocumentTitle('ビス締結基準', 'standard_001.pdf') // => 'ビス締結基準 - standard_001.pdf'
 */
export const formatDocumentTitle = (title: string | undefined | null, fileName: string | undefined | null): string => {
  // Handle edge cases
  if (!title && !fileName) {
    return '';
  }
  if (!title) {
    return fileName || '';
  }
  if (!fileName) {
    return title;
  }

  // Normalize for comparison (trim whitespace, lowercase)
  const normalizedTitle = title.trim().toLowerCase();
  const normalizedFileName = fileName.trim().toLowerCase();

  // Case 1: Title equals fileName (case-insensitive)
  if (normalizedTitle === normalizedFileName) {
    return title;
  }

  // Case 2: Title equals fileName without extension
  const fileNameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
  const normalizedFileNameWithoutExt = fileNameWithoutExt.trim().toLowerCase();

  if (normalizedTitle === normalizedFileNameWithoutExt) {
    return title;
  }

  // Case 3: Title and fileName are different - show both
  return `${title} - ${fileName}`;
};

/**
 * Get display title for document, with optional subtitle.
 * This is useful when you want to display title and fileName separately but
 * avoid redundancy.
 *
 * @param title - LLM-generated or document title
 * @param fileName - Original file name
 * @returns Object with main title and optional subtitle
 */
export const getDocumentDisplayParts = (
  title: string | undefined | null,
  fileName: string | undefined | null
): { main: string; subtitle: string | null } => {
  // Handle edge cases
  if (!title && !fileName) {
    return { main: '', subtitle: null };
  }
  if (!title) {
    return { main: fileName || '', subtitle: null };
  }
  if (!fileName) {
    return { main: title, subtitle: null };
  }

  // Normalize for comparison
  const normalizedTitle = title.trim().toLowerCase();
  const normalizedFileName = fileName.trim().toLowerCase();
  const fileNameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
  const normalizedFileNameWithoutExt = fileNameWithoutExt.trim().toLowerCase();

  // If title equals fileName or fileName without extension, no subtitle needed
  if (normalizedTitle === normalizedFileName || normalizedTitle === normalizedFileNameWithoutExt) {
    return { main: title, subtitle: null };
  }

  // Different - show title as main, fileName as subtitle
  return { main: title, subtitle: fileName };
};
